// Thin typed client for the Phoenix server. The PocketBase user token from the
// login (see auth.ts) rides along as a bearer Authorization header, and every
// request passes the one place where an expired session is handled.

import { logout, refresh, type Session } from "./auth";
import { SERVER_URL, getToken } from "./config";

export interface Task {
  id: string;
  title: string;
  size: "s" | "m" | "l";
  points: number;
  done: boolean;
  /** Program-menu id — joins the row to its when/why/how in the program. */
  ref?: string;
}

export interface ProgramItem {
  id: string;
  title: string;
  category: string;
  slot: string;
  when_sv: string;
  why_sv: string;
  how_sv: string;
  points: number;
}

export interface Checkin {
  id?: string;
  date: string;
  slot: "morning" | "midday" | "evening" | "night";
  mood?: number;
  plan_size?: PlanSize;
}

export type PlanSize = "small" | "medium" | "large" | "rest";

export interface Today {
  date: string;
  checkin: Checkin | null;
  tasks: Task[];
  streak: number;
  streak_paused: boolean;
  points_total: number;
  bad_week_mode: boolean;
  brief_available: boolean;
}

export interface CheckinPayload {
  client_uuid: string;
  date: string;
  slot: Checkin["slot"];
  mood: number;
  plan_size: PlanSize;
  source: "pwa";
  weight_kg?: number;
  sleep_hours?: number;
  appetite?: number;
}

export interface Mute {
  topic: string;
  until?: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
  }
}

function headers(json = true): Record<string, string> {
  const h: Record<string, string> = {};
  if (json) h["Content-Type"] = "application/json";
  const token = getToken();
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h;
}

// One in-flight refresh at a time: a burst of parallel 401s (the Idag view
// fires several calls at once) must renew the session once, not five times.
let refreshing: Promise<Session | null> | null = null;

function refreshOnce(): Promise<Session | null> {
  if (!refreshing) {
    refreshing = refresh().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

/**
 * The single authed fetch. On 401: refresh once, retry once, and if the session
 * is truly gone, drop it and send Björn back to the welcome screen rather than
 * leaving him on a page that silently fails.
 */
async function authedFetch(path: string, init?: RequestInit, json = true): Promise<Response> {
  const send = (): Promise<Response> =>
    fetch(`${SERVER_URL}${path}`, { ...init, headers: headers(json) });
  const res = await send();
  if (res.status !== 401) return res;
  if (await refreshOnce()) {
    const retried = await send(); // headers() re-reads the renewed token
    if (retried.status !== 401) return retried;
  }
  logout();
  location.hash = "#/valkommen";
  return res;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authedFetch(path, init);
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { message?: string; error?: string };
      detail = body.message ?? body.error ?? detail;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export function getToday(): Promise<Today> {
  return request<Today>("/api/today");
}

export function postCheckin(payload: CheckinPayload): Promise<unknown> {
  return request("/api/checkin", { method: "POST", body: JSON.stringify(payload) });
}

export function markTaskDone(id: string): Promise<unknown> {
  return request(`/api/tasks/${encodeURIComponent(id)}/done`, { method: "POST" });
}

export interface Brief {
  week_start?: string;
  html: string;
}

export async function getLatestBrief(): Promise<Brief | null> {
  const res = await authedFetch("/api/brief/latest", undefined, false);
  if (res.status === 404) return null;
  if (!res.ok) throw new ApiError(res.status, res.statusText);
  const type = res.headers.get("content-type") ?? "";
  if (type.includes("application/json")) {
    const body = (await res.json()) as Brief & { html?: string };
    return body.html ? body : null;
  }
  const html = await res.text();
  return html.trim() ? { html } : null;
}

export function getPushKey(): Promise<{ key: string }> {
  return request<{ key: string }>("/api/push/key");
}

export function postPushSubscribe(sub: {
  endpoint: string;
  p256dh: string;
  auth: string;
  ua: string;
  role: "patient";
}): Promise<unknown> {
  return request("/api/push/subscribe", { method: "POST", body: JSON.stringify(sub) });
}

export function getMutes(): Promise<Mute[]> {
  return request<Mute[]>("/api/mutes");
}

export function clearMute(topic: string): Promise<unknown> {
  return request(`/api/mutes/${encodeURIComponent(topic)}`, { method: "DELETE" });
}

// ── statistics, calendar, readings, patient ─────────────────────────────────
// The numbers below go to Björn himself (the decided direction of 2026-08-18).
// The server owns every derived figure: GKI is computed there, the weight
// direction arrives as a finished plain sentence, and `direction_sv` is null
// when there are too few points to say anything honest.

export interface WeightPoint {
  date: string;
  kg: number;
}

export interface GkiPoint {
  date: string;
  gki: number;
  zone: string;
}

export interface MoodPoint {
  date: string;
  mood: number;
}

export interface CycleInfo {
  /** 1-based day count since the latest infusion. */
  day: number;
  ofDays: number | null;
  lastInfusionDate: string;
}

export interface Stats {
  cycle: CycleInfo | null;
  weight: {
    points: WeightPoint[];
    smoothed: WeightPoint[];
    direction_sv: string | null;
  };
  gki: GkiPoint[];
  mood: MoodPoint[];
  streak_days: number;
  tasks_done_7d: number;
  latest_brief: { week_of: string } | null;
  /** The care profile's GKI display zones, when the server sends them. */
  gki_zones?: Array<{ max: number | null; id: string; label_sv: string }>;
}

export function getStats(): Promise<Stats> {
  return request<Stats>("/api/stats");
}

// ── the kitchen: menu + shopping list ───────────────────────────────────────

export interface RecipeIngredient {
  name: string;
  qty: number;
  unit: string;
  cat: string;
}

export interface Recipe {
  id: string;
  title_sv: string;
  soft: boolean;
  kcal: number;
  protein_g: number;
  why_sv: string;
  ingredients: RecipeIngredient[];
  steps_sv: string[];
  tip_sv?: string;
}

export interface MenuMeal {
  slot: "frukost" | "mellanmal_fm" | "lunch" | "mellanmal_em" | "middag";
  recipe: Recipe;
}

export interface MenuDay {
  date: string;
  soft: boolean;
  meals: MenuMeal[];
  kcal_total: number;
  protein_total_g: number;
}

export function getMenu(days = 7): Promise<{ days: MenuDay[]; protein_target_g: [number, number] | null; safety_sv?: string }> {
  return request(`/api/menu?days=${days}`);
}

export interface ShoppingGroup {
  category: string;
  label_sv: string;
  items: { name: string; qty: number; unit: string }[];
}

export function getShoppingList(days = 7): Promise<{ from: string; to: string; groups: ShoppingGroup[] }> {
  return request(`/api/shopping-list?days=${days}`);
}

export function getProgram(): Promise<{ items: ProgramItem[] }> {
  return request<{ items: ProgramItem[] }>("/api/program");
}

export type CalendarEventKind =
  | "infusion"
  | "labs"
  | "appointment"
  | "measurement"
  | "fasting"
  | "shopping"
  | "other";

export interface CalendarEvent {
  id: string;
  date: string;
  time: string;
  kind: CalendarEventKind;
  title: string;
  notes: string;
  done: boolean;
}

/**
 * An expected window from the care profile — a placeholder to confirm with the
 * care team, never a prediction. `from`/`to` are cycle days.
 */
export interface CalendarPhase {
  id: string;
  label_sv: string;
  note_sv: string;
  from: number;
  to: number;
  active: boolean;
  // Forecast personalization: the profile's own window plus whether his
  // recorded days have tuned it. Absent on older servers — treat as untuned.
  base_from?: number;
  base_to?: number;
  adjusted?: boolean;
}

export interface CalendarInfo {
  date: string;
  cycle: CycleInfo | null;
  events: CalendarEvent[];
  phases: CalendarPhase[];
}

export function getCalendarInfo(days?: number): Promise<CalendarInfo> {
  const query = days ? `?days=${encodeURIComponent(String(days))}` : "";
  return request<CalendarInfo>(`/api/calendar${query}`);
}

export function postCalendarEvent(input: {
  date: string;
  kind: CalendarEventKind;
  title: string;
}): Promise<unknown> {
  return request("/api/calendar", { method: "POST", body: JSON.stringify(input) });
}

export interface ReadingPayload {
  client_uuid: string;
  glucose_mmol?: number;
  ketones_mmol?: number;
}

export interface ReadingResult {
  ok: boolean;
  reading: {
    date: string;
    slot: Checkin["slot"];
    glucose_mmol: number | null;
    ketones_mmol: number | null;
    gki: number | null;
  };
  zone: string | null;
}

export function postReading(payload: ReadingPayload): Promise<ReadingResult> {
  return request<ReadingResult>("/api/readings", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export interface PatientPatch {
  height_cm?: number;
  weight_ref_kg?: number;
}

export function patchPatient(patch: PatientPatch): Promise<unknown> {
  return request("/api/patient", { method: "PATCH", body: JSON.stringify(patch) });
}

// ── text chat over SSE ──────────────────────────────────────────────────────
// POST /api/chat streams the persona reply as SSE in the same shape the
// gateway seam uses: `data: {json}` lines whose choices[0].delta.content
// carries text pieces, terminated by `data: [DONE]`.

export interface ChatRequest {
  message: string;
  session_id: string;
  /** Optional photo ("kan jag äta det här?") — raw base64, no data: prefix. */
  image_base64?: string;
  image_media_type?: string;
  persona?: string;
}

export async function streamChat(
  req: ChatRequest,
  onPiece: (text: string) => void,
  signal?: AbortSignal
): Promise<void> {
  const res = await authedFetch("/api/chat", {
    method: "POST",
    body: JSON.stringify(req),
    ...(signal ? { signal } : {}),
  });
  if (!res.ok || !res.body) {
    throw new ApiError(res.status, res.statusText);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE events are separated by blank lines; lines start with "data: ".
    let idx: number;
    while ((idx = buffer.indexOf("\n")) >= 0) {
      const line = buffer.slice(0, idx).replace(/\r$/, "");
      buffer = buffer.slice(idx + 1);
      if (!line.startsWith("data:")) continue;
      const data = line.slice(5).trim();
      if (data === "[DONE]") return;
      if (!data) continue;
      try {
        const json = JSON.parse(data) as {
          choices?: Array<{ delta?: { content?: string } }>;
          text?: string;
        };
        const piece = json.choices?.[0]?.delta?.content ?? json.text ?? "";
        if (piece) onPiece(piece);
      } catch {
        /* ignore malformed keepalive lines */
      }
    }
  }
}
